import { useState } from "react";
import { Tooltip } from "@monolith-forensics/monolith-ui";

import {
  CopyableRow,
  getLatestRecord,
  LatestCocSection,
  TooltipBody,
} from "@/components/CocTooltip";
import { useGetStorageCocRecords } from "@/Features/Storage/storage.query";

interface StorageCurrentLocationCellProps {
  rowData: {
    storage_id?: number;
    case_assignment_id?: number | null;
    location_name?: string;
    location_path?: string;
  };
}

const StorageCurrentLocationCell: React.FC<StorageCurrentLocationCellProps> = ({
  rowData,
}) => {
  const [hovered, setHovered] = useState(false);
  // isPending, not isLoading: isLoading is false while the query is disabled
  // (pre-hover) with no data yet, which would flash the definitive "No chain
  // of custody records" line. retry: false — the tooltip already shows the
  // failure, and each retried request fires another interceptor error toast.
  const { data, isPending, isError } = useGetStorageCocRecords(
    rowData.storage_id,
    rowData.case_assignment_id ?? null,
    {
      enabled: hovered,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  if (!rowData.location_name) return null;

  const content = (
    <TooltipBody>
      <div className="tooltip-title">Location info</div>
      <CopyableRow label="Current Location" value={rowData.location_name} />
      <CopyableRow label="Location Path" value={rowData.location_path} />
      <LatestCocSection
        latest={getLatestRecord(data)}
        isPending={isPending}
        isError={isError}
      />
    </TooltipBody>
  );

  return (
    <Tooltip
      content={content}
      hoverableContent
      delayDuration={400}
      side="bottom"
      align="start"
      sideOffset={5}
    >
      <span
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
      >
        {rowData.location_name}
      </span>
    </Tooltip>
  );
};

export default StorageCurrentLocationCell;
